import { Plus } from 'lucide-react'
import type { QuizBlock } from '@/types/blocks'
import { Button, Label } from '@teach/ui'
import { QUIZ_LIMITS } from '@/features/editor/constants/fieldLimits'
import { useContentLang } from '@/features/editor/hooks/useContentLang'
import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
import { useStableKeys } from '@/features/editor/hooks/useStableKeys'
import {
  addAnswer,
  getBody,
  removeAnswerAt,
  setAnswerText,
  setCorrectness,
  setQuestion,
} from './quizBodySync'
import { FillBlankStatement } from './FillBlankStatement'
import { QuizAnswerRow } from './QuizAnswerRow'
import { QuizFeedbackFields } from './QuizFeedbackFields'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: QuizBlock
  onChange: (next: QuizBlock) => void
}

// fill_blank: statement with a single `___` gap + up to 5 word options,
// exactly one correct (single-select, like quiz-single).
export const FillBlankQuizEditor = ({ block, onChange }: Props) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const lang = useContentLang()
  const body = getBody(block, lang)
  const answers = body.answers
  const { keys, removeAt } = useStableKeys(answers.length, 'fb')
  // Правильное слово удалить нельзя (реф: `!word.isCorrect && rows.length > 2`):
  // без него блок молча становится непубликуемым — бек требует ровно один правильный.
  const canRemove = answers.length > QUIZ_LIMITS.OPTIONS_MIN

  const add = () => {
    if (answers.length >= QUIZ_LIMITS.FILL_BLANK_OPTIONS_MAX) return
    onChange(addAnswer(block, lang))
  }

  const remove = (index: number) => {
    removeAt(index)
    onChange(removeAnswerAt(block, index))
  }

  // single-correct: selecting one clears the rest
  const toggleCorrect = (index: number) => {
    onChange(setCorrectness(block, index, !answers[index].isCorrect, true))
  }

  return (
    <div className="space-y-4">
      <FillBlankStatement
        value={body.question ?? ''}
        onChange={(next) => onChange(setQuestion(block, lang, next))}
      />

      <section className="space-y-3">
        <Label className="text-foreground font-medium">{m.answerVariants}</Label>
        <div className="flex flex-col gap-2">
          {answers.map((a, i) => (
            <QuizAnswerRow
              key={keys[i]}
              index={i}
              answer={a}
              wordMax={QUIZ_LIMITS.BLANK_WORD_WORDS}
              charMax={QUIZ_LIMITS.BLANK_WORD_CHARS}
              canRemove={canRemove ? !a.isCorrect : false}
              onChangeText={(idx, text) => onChange(setAnswerText(block, lang, idx, text))}
              onToggleCorrect={toggleCorrect}
              onRemove={remove}
            />
          ))}
          {answers.length < QUIZ_LIMITS.FILL_BLANK_OPTIONS_MAX ? (
            <Button variant="secondary" size="sm" onClick={add}>
              <Plus className="mr-1.5 h-4 w-4" />
              {m.addVariant}
            </Button>
          ) : null}
        </div>
      </section>

      <QuizFeedbackFields
        block={block}
        onChange={onChange}
        placeholder={cm.fillBlankFeedbackPlaceholder}
      />
    </div>
  )
}
